import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import { BaseLayout } from '@/components/layout/BaseLayout';
import { ThemedCard } from '@/components/ui/themed-card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  PlusIcon,
  ArrowLeftIcon,
  TargetIcon,
  CheckCircleIcon,
  DollarSignIcon,
  ClockIcon,
  UsersIcon,
  TrendingUpIcon,
  FlameIcon,
  TrophyIcon,
  EyeIcon,
  PlayIcon,
  PauseIcon,
  CheckIcon,
  XIcon,
  ExternalLinkIcon,
  FileTextIcon,
  CalendarIcon,
  CreditCardIcon,
  AlertTriangleIcon
} from 'lucide-react';

interface Raffle {
  id: string;
  title: string;
  description: string;
  type: 'numbers' | 'names' | 'animals' | 'custom';
  status: 'draft' | 'active' | 'completed' | 'cancelled';
  total_spots: number;
  price_per_spot: number;
  prize_description: string;
  draw_date: string;
  winner_numbers?: number[];
  winner_user_id?: string;
  created_at: string;
  updated_at: string;
}

interface Participation {
  id: string;
  raffle_id: string;
  user_id: string;
  spot_number: string;
  status: 'reserved' | 'pending_payment' | 'paid' | 'cancelled';
  payment_proof_url?: string;
  reserved_at: string;
  paid_at?: string;
  user?: {
    email: string;
    full_name: string;
  };
}

interface DashboardStats {
  totalRaffles: number;
  activeRaffles: number;
  completedRaffles: number;
  totalRevenue: number;
  pendingPayments: number;
  totalParticipants: number;
  averageOccupancy: number;
  recentSales: number;
}

interface UserProfile {
  credits_balance: number;
  full_name: string;
  phone: string;
  cpf: string;
}

export const CreatorDashboardPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [raffles, setRaffles] = useState<Raffle[]>([]);
  const [selectedRaffle, setSelectedRaffle] = useState<Raffle | null>(null);
  const [participations, setParticipations] = useState<Participation[]>([]);
  const [stats, setStats] = useState<DashboardStats>({
    totalRaffles: 0,
    activeRaffles: 0,
    completedRaffles: 0,
    totalRevenue: 0,
    pendingPayments: 0,
    totalParticipants: 0,
    averageOccupancy: 0,
    recentSales: 0
  });
  const [filter, setFilter] = useState<'all' | 'active' | 'draft' | 'completed'>('all');
  const [paymentFilter, setPaymentFilter] = useState<'all' | 'pending' | 'approved'>('pending');
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [activationDialog, setActivationDialog] = useState<{
    open: boolean;
    raffleId: string;
    raffleTitle: string;
  }>({ open: false, raffleId: '', raffleTitle: '' });
  const [activating, setActivating] = useState(false);

  useEffect(() => {
    if (user) {
      fetchDashboardData();
      fetchUserProfile();
    }
  }, [user]);

  useEffect(() => {
    if (selectedRaffle) {
      fetchRaffleParticipations(selectedRaffle.id);
    }
  }, [selectedRaffle]);

  const fetchUserProfile = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('credits_balance, full_name, phone, cpf')
        .eq('id', user?.id)
        .single();

      if (error) throw error;
      setUserProfile(data);
    } catch (err: any) {
      console.error('Error fetching user profile:', err);
    }
  };

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Fetch user's raffles
      const { data: rafflesData, error: rafflesError } = await supabase
        .from('raffles')
        .select('*')
        .eq('creator_id', user?.id)
        .order('created_at', { ascending: false });

      if (rafflesError) throw rafflesError;

      console.log('Fetched raffles:', rafflesData);
      setRaffles(rafflesData || []);

      // Calculate statistics
      if (rafflesData) {
        await calculateStats(rafflesData);
      }
    } catch (err: any) {
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = async (raffles: Raffle[]) => {
    const stats: DashboardStats = {
      totalRaffles: raffles.length,
      activeRaffles: raffles.filter(r => r.status === 'active').length,
      completedRaffles: raffles.filter(r => r.status === 'completed').length,
      totalRevenue: 0,
      pendingPayments: 0,
      totalParticipants: 0,
      averageOccupancy: 0,
      recentSales: 0
    };

    // Fetch all participations for user's raffles
    const raffleIds = raffles.map(r => r.id);
    if (raffleIds.length > 0) {
      const { data: participationsData, error } = await supabase
        .from('participations')
        .select('*')
        .in('raffle_id', raffleIds);

      if (!error && participationsData) {
        // Calculate revenue
        participationsData.forEach(p => {
          if (p.status === 'paid') {
            const raffle = raffles.find(r => r.id === p.raffle_id);
            if (raffle) {
              stats.totalRevenue += raffle.price_per_spot;
            }
          }
          if (p.status === 'pending_payment') {
            stats.pendingPayments++;
          }
        });

        // Calculate participants
        const uniqueParticipants = new Set(participationsData.map(p => p.user_id));
        stats.totalParticipants = uniqueParticipants.size;

        // Calculate average occupancy
        const activeRaffles = raffles.filter(r => r.status === 'active');
        if (activeRaffles.length > 0) {
          let totalOccupancy = 0;
          activeRaffles.forEach(raffle => {
            const raffleParticipations = participationsData.filter(
              p => p.raffle_id === raffle.id && 
              (p.status === 'paid')
            );
            const soldSpots = raffleParticipations.reduce((sum, p) => sum + 1, 0);
            totalOccupancy += (soldSpots / raffle.total_spots) * 100;
          });
          stats.averageOccupancy = totalOccupancy / activeRaffles.length;
        }

        // Recent sales (last 7 days)
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        stats.recentSales = participationsData.filter(p => {
          const paidDate = p.paid_at ? new Date(p.paid_at) : new Date(p.reserved_at);
          return paidDate >= sevenDaysAgo && 
                 (p.status === 'paid');
        }).length;
      }
    }

    setStats(stats);
  };

  const fetchRaffleParticipations = async (raffleId: string) => {
    try {
      // First get all participations for this raffle
      const { data: participationsData, error: participationsError } = await supabase
        .from('participations')
        .select('*')
        .eq('raffle_id', raffleId)
        .order('created_at', { ascending: false });

      if (participationsError) throw participationsError;

      // Then get user data for each participation
      const participationsWithUsers = await Promise.all(
        (participationsData || []).map(async (participation: any) => {
          const { data: userData, error: userError } = await supabase
            .from('profiles')
            .select('email, full_name')
            .eq('id', participation.user_id)
            .single();

          return {
            ...participation,
            user: userData || { email: 'N/A', full_name: 'Usuário Desconhecido' }
          };
        })
      );

      setParticipations(participationsWithUsers);
    } catch (err: any) {
      console.error('Error fetching participations:', err);
    }
  };

  const handleValidatePayment = async (participationId: string, approved: boolean) => {
    try {
      const { error } = await supabase
        .from('participations')
        .update({
          status: approved ? 'approved' : 'rejected',
          paid_at: approved ? new Date().toISOString() : null
        })
        .eq('id', participationId);

      if (error) throw error;

      // Refresh data
      if (selectedRaffle) {
        fetchRaffleParticipations(selectedRaffle.id);
      }
      fetchDashboardData();
    } catch (err: any) {
      console.error('Error validating payment:', err);
    }
  };

  const handleRaffleAction = async (raffleId: string, action: 'pause' | 'activate' | 'complete' | 'cancel') => {
    try {
      if (action === 'activate') {
        // For activation, show confirmation dialog
        const raffle = raffles.find(r => r.id === raffleId);
        if (!raffle) return;

        setActivationDialog({
          open: true,
          raffleId,
          raffleTitle: raffle.title
        });
        return;
      }

      // For other actions, use the status update function
      let newStatus: Raffle['status'];
      switch (action) {
        case 'pause':
          newStatus = 'draft';
          break;
        case 'complete':
          newStatus = 'completed';
          break;
        case 'cancel':
          newStatus = 'cancelled';
          break;
        default:
          return;
      }

      const { data, error } = await supabase.rpc('update_raffle_status', {
        p_raffle_id: raffleId,
        p_new_status: newStatus
      });

      if (error) throw error;

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to update raffle status');
      }

      // Refresh data
      fetchDashboardData();
      setSelectedRaffle(null);
    } catch (err: any) {
      console.error(`Error ${action} raffle:`, err);
    }
  };

  const handleActivateRaffle = async () => {
    try {
      setActivating(true);
      
      const { data, error } = await supabase.rpc('activate_raffle_with_payment', {
        p_raffle_id: activationDialog.raffleId,
        p_activation_fee: 10.00
      });

      if (error) throw error;

      if (!data?.success) {
        throw new Error(data?.error || 'Failed to activate raffle');
      }

      // Refresh user profile and dashboard data
      await Promise.all([
        fetchUserProfile(),
        fetchDashboardData()
      ]);
      
      setActivationDialog({ open: false, raffleId: '', raffleTitle: '' });
      setSelectedRaffle(null);
      
    } catch (err: any) {
      console.error('Error activating raffle:', err);
    } finally {
      setActivating(false);
    }
  };

  const handleDrawRaffle = async (raffleId: string) => {
    const raffle = raffles.find(r => r.id === raffleId);
    if (!raffle) return;

    // Navigate to drawing system
    navigate(`/raffle/${raffleId}/draw`);
  };

  const formatCurrency = (value: number) => {
    if (value === null || value === undefined || isNaN(value)) {
      return 'R$ 0,00';
    }
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const translateStatus = (status: string) => {
    const statusMap: { [key: string]: string } = {
      'draft': 'Rascunho',
      'active': 'Ativa',
      'completed': 'Finalizada',
      'cancelled': 'Cancelada'
    };
    return statusMap[status] || status;
  };

  const getFilteredRaffles = () => {
    if (filter === 'all') return raffles;
    return raffles.filter(r => r.status === filter);
  };

  const getFilteredParticipations = () => {
    if (paymentFilter === 'all') return participations;
    if (paymentFilter === 'pending') {
      return participations.filter(p => p.status === 'pending_payment' || p.status === 'reserved');
    }
    return participations.filter(p => p.status === 'paid');
  };

  if (loading) {
    return (
      <BaseLayout variant="centered">
        <div className="min-h-[50vh] flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando dashboard...</p>
          </div>
        </div>
      </BaseLayout>
    );
  }

  return (
    <BaseLayout variant="wide">
      <div className="space-y-8">
        {/* Header */}
        <motion.div
          className="flex items-center justify-between py-6"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div>
            <h1 className="text-4xl font-bold text-foreground">
              Minhas Rifas 🎯
            </h1>
            <p className="text-xl text-muted-foreground mt-2">
              Crie, gerencie e acompanhe suas rifas
            </p>
          </div>
          <div className="flex gap-3">
            <Button 
              onClick={() => navigate('/create-raffle')}
              size="lg"
              className="bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-600/90"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Criar Nova Rifa
            </Button>
          </div>
        </motion.div>

        {/* Stats Grid */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-4"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <ThemedCard className="p-6 bg-gradient-to-br from-blue-500/10 to-indigo-600/10 border-blue-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-600 text-sm font-medium mb-1">Total de Rifas</p>
                <p className="text-3xl font-bold text-foreground">{stats.totalRaffles}</p>
              </div>
              <div className="h-12 w-12 bg-blue-500/20 rounded-full flex items-center justify-center">
                <TargetIcon className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </ThemedCard>

          <ThemedCard className="p-6 bg-gradient-to-br from-green-500/10 to-emerald-600/10 border-green-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-600 text-sm font-medium mb-1">Rifas Ativas</p>
                <p className="text-3xl font-bold text-foreground">{stats.activeRaffles}</p>
              </div>
              <div className="h-12 w-12 bg-green-500/20 rounded-full flex items-center justify-center">
                <CheckCircleIcon className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </ThemedCard>

          <ThemedCard className="p-6 bg-gradient-to-br from-purple-500/10 to-violet-600/10 border-purple-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-600 text-sm font-medium mb-1">Receita Total</p>
                <p className="text-3xl font-bold text-foreground">{formatCurrency(stats.totalRevenue)}</p>
              </div>
              <div className="h-12 w-12 bg-purple-500/20 rounded-full flex items-center justify-center">
                <DollarSignIcon className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </ThemedCard>

          <ThemedCard className="p-6 bg-gradient-to-br from-orange-500/10 to-red-600/10 border-orange-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-600 text-sm font-medium mb-1">Pagamentos Pendentes</p>
                <p className="text-3xl font-bold text-foreground">{stats.pendingPayments}</p>
              </div>
              <div className="h-12 w-12 bg-orange-500/20 rounded-full flex items-center justify-center">
                <ClockIcon className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </ThemedCard>

          <ThemedCard className="p-6 bg-gradient-to-br from-teal-500/10 to-cyan-600/10 border-teal-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-teal-600 text-sm font-medium mb-1">Total de Participantes</p>
                <p className="text-3xl font-bold text-foreground">{stats.totalParticipants}</p>
              </div>
              <div className="h-12 w-12 bg-teal-500/20 rounded-full flex items-center justify-center">
                <UsersIcon className="h-6 w-6 text-teal-600" />
              </div>
            </div>
          </ThemedCard>

          <ThemedCard className="p-6 bg-gradient-to-br from-indigo-500/10 to-blue-600/10 border-indigo-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-indigo-600 text-sm font-medium mb-1">Ocupação Média</p>
                <p className="text-3xl font-bold text-foreground">{stats.averageOccupancy.toFixed(1)}%</p>
              </div>
              <div className="h-12 w-12 bg-indigo-500/20 rounded-full flex items-center justify-center">
                <TrendingUpIcon className="h-6 w-6 text-indigo-600" />
              </div>
            </div>
          </ThemedCard>

          <ThemedCard className="p-6 bg-gradient-to-br from-red-500/10 to-pink-600/10 border-red-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-600 text-sm font-medium mb-1">Vendas Recentes (7d)</p>
                <p className="text-3xl font-bold text-foreground">{stats.recentSales}</p>
              </div>
              <div className="h-12 w-12 bg-red-500/20 rounded-full flex items-center justify-center">
                <FlameIcon className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </ThemedCard>

          <ThemedCard className="p-6 bg-gradient-to-br from-yellow-500/10 to-amber-600/10 border-yellow-500/20">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-yellow-600 text-sm font-medium mb-1">Finalizadas</p>
                <p className="text-3xl font-bold text-foreground">{stats.completedRaffles}</p>
              </div>
              <div className="h-12 w-12 bg-yellow-500/20 rounded-full flex items-center justify-center">
                <TrophyIcon className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </ThemedCard>
        </motion.div>

        {/* Raffles Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <ThemedCard className="p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-semibold text-foreground">Suas Rifas</h2>
              <div className="flex gap-2">
                <Button 
                  variant={filter === 'all' ? 'default' : 'outline'}
                  onClick={() => setFilter('all')}
                  size="sm"
                >
                  Todas
                </Button>
                <Button 
                  variant={filter === 'active' ? 'default' : 'outline'}
                  onClick={() => setFilter('active')}
                  size="sm"
                >
                  Ativas
                </Button>
                <Button 
                  variant={filter === 'draft' ? 'default' : 'outline'}
                  onClick={() => setFilter('draft')}
                  size="sm"
                >
                  Rascunho
                </Button>
                <Button 
                  variant={filter === 'completed' ? 'default' : 'outline'}
                  onClick={() => setFilter('completed')}
                  size="sm"
                >
                  Finalizadas
                </Button>
              </div>
            </div>

            <div className="grid gap-6">
              {getFilteredRaffles().length === 0 ? (
                <div className="text-center py-12">
                  <div className="h-20 w-20 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                    <TargetIcon className="h-10 w-10 text-muted-foreground" />
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">Nenhuma rifa encontrada</h3>
                  <p className="text-muted-foreground mb-4">
                    {filter === 'all' 
                      ? 'Você ainda não criou nenhuma rifa.' 
                      : `Não há rifas com status "${translateStatus(filter)}".`
                    }
                  </p>
                  <Button onClick={() => navigate('/create-raffle')}>
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Criar Minha Primeira Rifa
                  </Button>
                </div>
              ) : (
                getFilteredRaffles().map(raffle => (
                  <ThemedCard 
                    key={raffle.id} 
                    className={`overflow-hidden transition-all ${
                      selectedRaffle?.id === raffle.id 
                        ? 'ring-2 ring-primary border-primary bg-primary/5' 
                        : 'hover:shadow-md hover:border-primary/30'
                    }`}
                  >
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="text-xl font-semibold text-foreground">{raffle.title}</h3>
                            <Badge 
                              variant={raffle.status === 'active' ? 'default' : 
                                      raffle.status === 'completed' ? 'secondary' : 
                                      raffle.status === 'draft' ? 'outline' : 'destructive'}
                            >
                              {translateStatus(raffle.status)}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mb-4 line-clamp-2">{raffle.description}</p>
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                        <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                          <div className="h-8 w-8 bg-yellow-500/20 rounded-full flex items-center justify-center">
                            <TrophyIcon className="h-4 w-4 text-yellow-600" />
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Prêmio</p>
                            <p className="text-sm font-medium">{raffle.prize_description}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                          <div className="h-8 w-8 bg-blue-500/20 rounded-full flex items-center justify-center">
                            <TargetIcon className="h-4 w-4 text-blue-600" />
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Total de Cotas</p>
                            <p className="text-sm font-medium">{raffle.total_spots}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                          <div className="h-8 w-8 bg-green-500/20 rounded-full flex items-center justify-center">
                            <DollarSignIcon className="h-4 w-4 text-green-600" />
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Preço por Cota</p>
                            <p className="text-sm font-medium">{formatCurrency(raffle.price_per_spot)}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                          <div className="h-8 w-8 bg-purple-500/20 rounded-full flex items-center justify-center">
                            <CalendarIcon className="h-4 w-4 text-purple-600" />
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Sorteio</p>
                            <p className="text-sm font-medium">{formatDate(raffle.draw_date).split(' ')[0]}</p>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex flex-wrap gap-2 pt-4 border-t">
                        <Button 
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/raffle/${raffle.id}`)}
                        >
                          <EyeIcon className="h-4 w-4 mr-2" />
                          {raffle.status === 'draft' ? 'Visualizar' : 'Ver Página'}
                        </Button>
                        
                        <Button 
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedRaffle(selectedRaffle?.id === raffle.id ? null : raffle)}
                        >
                          <UsersIcon className="h-4 w-4 mr-2" />
                          Gerenciar Participações
                        </Button>
                        
                        {raffle.status === 'draft' && (
                          <>
                            <Button 
                              variant="outline"
                              size="sm"
                              onClick={() => navigate(`/create-raffle?edit=${raffle.id}`)}
                            >
                              <FileTextIcon className="h-4 w-4 mr-2" />
                              Editar
                            </Button>
                            <Button 
                              variant="default"
                              size="sm"
                              onClick={() => handleRaffleAction(raffle.id, 'activate')}
                              disabled={!userProfile || userProfile.credits_balance < 10}
                              className={!userProfile || userProfile.credits_balance < 10 ? 'opacity-50 cursor-not-allowed' : ''}
                            >
                              <PlayIcon className="h-4 w-4 mr-2" />
                              Ativar (R$ 10,00)
                            </Button>
                          </>
                        )}
                        
                        {raffle.status === 'active' && (
                          <>
                            <Button 
                              variant="outline"
                              size="sm"
                              onClick={() => handleRaffleAction(raffle.id, 'pause')}
                            >
                              <PauseIcon className="h-4 w-4 mr-2" />
                              Pausar
                            </Button>
                            <Button 
                              variant="default"
                              size="sm"
                              onClick={() => handleDrawRaffle(raffle.id)}
                              className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                            >
                              <TrophyIcon className="h-4 w-4 mr-2" />
                              Realizar Sorteio
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </ThemedCard>
                ))
              )}
            </div>
          </ThemedCard>
        </motion.div>

        {selectedRaffle && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <ThemedCard className="p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-semibold text-foreground">
                  Participações - {selectedRaffle.title}
                </h2>
                <div className="flex gap-2">
                  <Button 
                    variant={paymentFilter === 'all' ? 'default' : 'outline'}
                    onClick={() => setPaymentFilter('all')}
                    size="sm"
                  >
                    Todas
                  </Button>
                  <Button 
                    variant={paymentFilter === 'pending' ? 'default' : 'outline'}
                    onClick={() => setPaymentFilter('pending')}
                    size="sm"
                  >
                    Pendentes
                  </Button>
                  <Button 
                    variant={paymentFilter === 'approved' ? 'default' : 'outline'}
                    onClick={() => setPaymentFilter('approved')}
                    size="sm"
                  >
                    Aprovadas
                  </Button>
                </div>
              </div>

              <div className="space-y-4">
                {getFilteredParticipations().map(participation => (
                  <ThemedCard key={participation.id} className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 bg-primary/10 rounded-full flex items-center justify-center">
                          <UsersIcon className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <div className="font-medium text-foreground">
                            {participation.user?.full_name || 'Unknown'}
                          </div>
                          <div className="text-sm text-muted-foreground">
                            {participation.user?.email}
                          </div>
                        </div>
                      </div>
                      <Badge 
                        variant={participation.status === 'paid' ? 'default' : 
                                participation.status === 'pending_payment' ? 'secondary' : 'destructive'}
                      >
                        {participation.status}
                      </Badge>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div>
                        <div className="text-sm text-muted-foreground mb-2">Números:</div>
                        <div className="flex flex-wrap gap-1">
                          {[participation.spot_number].map(num => (
                            <span key={num} className="bg-primary/10 text-primary px-2 py-1 rounded text-xs font-medium">
                              {num.toString().padStart(3, '0')}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2">
                          <ClockIcon className="h-4 w-4 text-muted-foreground" />
                          <span className="text-muted-foreground">Reserved:</span>
                          <span>{formatDate(participation.reserved_at)}</span>
                        </div>
                        {participation.paid_at && (
                          <div className="flex items-center gap-2">
                            <CheckIcon className="h-4 w-4 text-green-600" />
                            <span className="text-muted-foreground">Pago em:</span>
                            <span>{formatDate(participation.paid_at)}</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <DollarSignIcon className="h-4 w-4 text-green-600" />
                          <span className="text-muted-foreground">Total:</span>
                          <span className="font-medium">
                            {formatCurrency(selectedRaffle.price_per_spot)}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    {participation.status === 'pending_payment' && (
                      <div className="flex flex-wrap gap-2 pt-4 border-t">
                        {participation.payment_proof_url && (
                          <Button 
                            variant="outline"
                            size="sm"
                            asChild
                          >
                            <a 
                              href={participation.payment_proof_url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                            >
                              <ExternalLinkIcon className="h-4 w-4 mr-2" />
                              Ver Comprovante
                            </a>
                          </Button>
                        )}
                        <Button 
                          size="sm"
                          onClick={() => handleValidatePayment(participation.id, true)}
                          className="bg-green-600 hover:bg-green-700"
                        >
                          <CheckIcon className="h-4 w-4 mr-2" />
                          Aprovar
                        </Button>
                        <Button 
                          variant="destructive"
                          size="sm"
                          onClick={() => handleValidatePayment(participation.id, false)}
                        >
                          <XIcon className="h-4 w-4 mr-2" />
                          Rejeitar
                        </Button>
                      </div>
                    )}
                  </ThemedCard>
                ))}
              </div>
            </ThemedCard>
          </motion.div>
        )}

        {/* Activation Confirmation Dialog */}
        <Dialog open={activationDialog.open} onOpenChange={(open) => 
          setActivationDialog(prev => ({ ...prev, open }))
        }>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangleIcon className="h-5 w-5 text-yellow-600" />
                Confirmar Ativação da Rifa
              </DialogTitle>
              <DialogDescription>
                Você está prestes a ativar a rifa <strong>"{activationDialog.raffleTitle}"</strong>.
                <br /><br />
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 my-4">
                  <div className="flex items-start gap-3">
                    <CreditCardIcon className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                    <div className="space-y-2">
                      <p className="font-semibold text-yellow-800 dark:text-yellow-200">
                        Taxa de Ativação: R$ 10,00
                      </p>
                      <p className="text-sm text-yellow-700 dark:text-yellow-300">
                        Saldo Atual: <span className="font-medium">{userProfile ? formatCurrency(userProfile.credits_balance || 0) : 'Carregando...'}</span>
                      </p>
                      <p className="text-sm text-yellow-700 dark:text-yellow-300">
                        Saldo Após Ativação: <span className="font-medium">{userProfile ? formatCurrency((userProfile.credits_balance || 0) - 10) : 'Carregando...'}</span>
                      </p>
                    </div>
                  </div>
                </div>
                Esta taxa será debitada do seu saldo de créditos. Após a ativação, a rifa ficará pública e os usuários poderão participar.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button 
                variant="outline" 
                onClick={() => setActivationDialog({ open: false, raffleId: '', raffleTitle: '' })}
                disabled={activating}
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleActivateRaffle}
                disabled={activating || !userProfile || userProfile.credits_balance < 10}
                className="bg-green-600 hover:bg-green-700"
              >
                {activating ? (
                  <div className="flex items-center gap-2">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Ativando...
                  </div>
                ) : (
                  <>Confirmar Ativação (R$ 10,00)</>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </BaseLayout>
  );
};